import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ApiErrors, StoreLoadingEnum} from 'types/global';
import {createApiError} from 'helpers/global';
import {RootState} from "store";
import {tenantsApi, TenantIdsQueryParams} from "api/tenants";

interface TenantIdsStateDto {
    loading: StoreLoadingEnum;
    error: string | null;
    data: string[];
    apiKey: string | null;
}

const initialState: TenantIdsStateDto = {
    loading: StoreLoadingEnum.idle,
    error: null,
    data: [],
    apiKey: null,
}

export const tenantIdsThunk = createAsyncThunk<
    string[],
    TenantIdsQueryParams & { signal?: AbortSignal },
    { rejectValue: ApiErrors }
>(
    'tenantIds/get',
    async (data, thunkAPI) => {
        try {
            return await tenantsApi.getTenantIds(data, data.signal);
        } catch (e) {
            if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
                return thunkAPI.rejectWithValue({message: 'canceled'});
            }
            // Tenant options are an enhancement: on failure the tenant filter
            // falls back to free-text input, so never redirect to /error.
            return thunkAPI.rejectWithValue({...createApiError(e), silent: true});
        }
    }
);

export const tenantIdsSlice = createSlice({
    name: 'tenantIds',
    initialState,
    reducers: {
        reset: (state) => Object.assign(state, initialState),
    },
    extraReducers: (builder) => {
        builder.addCase(tenantIdsThunk.pending, (state, action) => {
            state.loading = StoreLoadingEnum.pending;
            state.error = null;
            state.apiKey = action.meta.arg.apiKey ?? null;
        });
        builder.addCase(tenantIdsThunk.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = StoreLoadingEnum.loaded;
            state.error = null;
        });
        builder.addCase(tenantIdsThunk.rejected, (state, action) => {
            if (!action.payload || action.payload.message === 'canceled') return;

            state.data = [];
            state.loading = StoreLoadingEnum.loaded;
            state.error = action.payload.message ?? null;
        });
    },
});

export const selectTenantIdsSelector = (state: RootState) => state.tenantIds.data;
export const selectTenantIdsLoadedKeySelector = (state: RootState) =>
    state.tenantIds.loading === StoreLoadingEnum.loaded ? state.tenantIds.apiKey : null;

export default tenantIdsSlice;
